import * as SurveyDefinitionActions from "src/app/store/survey/actions";
import * as FromSurveyDefinition from "src/app/store/survey/selectors";
import * as FromRouter from "src/app/store/router/selectors";
import { CreateSurveyDefinitionDto, SurveyDefinitionDto, SurveyFieldDto } from "@api";
import { NavController, PopoverController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { AppRoutes } from "src/app/constants";
import { RootState } from "src/app/store";
import { filter, first, mergeMap, skipWhile } from "rxjs/operators";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Camera, CameraResultType, CameraSource, Photo } from "@capacitor/camera";
import { StorageService } from "src/app/services/storage/storage.service";
import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { UpdateSurveyDefinitionDto } from "src/app/services/api/model/update-survey-definition-dto";

@Component({
  selector: "app-survey-definition-form",
  templateUrl: "./survey-definition-form.page.html",
  styleUrls: ["./survey-definition-form.page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyDefinitionFormPage implements OnInit {
  public readonly routes: typeof AppRoutes = AppRoutes;
  public form: FormGroup;
  public image?: Photo;
  public imageSuccess?: Photo;
  public readonly breadcrumbs = [
    { name: this.i18n.instant("DASHBOARD.BREADCRUMB_NAME"), link: `/${AppRoutes.home}` },
    { name: this.i18n.instant("ADMIN_SURVEYS.BREADCRUMB_NAME"), link: `/${AppRoutes.adminSurveyDefinitions}` },
  ];
  public readonly loading$: Observable<boolean> = this.store.select(FromSurveyDefinition.selectLoading);
  public readonly surveyDefinitionSlug$: Observable<string> = this.store.select(FromRouter.selectSurveySlug);
  public surveyDefinition$: Observable<SurveyDefinitionDto> = this.surveyDefinitionSlug$.pipe(
    mergeMap(definitionSlug => this.store.select(FromSurveyDefinition.selectDefinitionBySlug(definitionSlug))),
  );

  constructor(
    private readonly store: Store<RootState>,
    private readonly i18n: TranslateService,
    private readonly navController: NavController,
    private readonly popoverController: PopoverController,
    private readonly storage: StorageService,
    private readonly changeDetector: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.form = new FormGroup({
      name: new FormControl(null, [Validators.required]),
      title: new FormControl(null, [Validators.required]),
      description: new FormControl(null, [Validators.required]),
      startButtonText: new FormControl(null, [Validators.required]),
      successTitle: new FormControl(null, [Validators.required]),
      successText: new FormControl(null, [Validators.required]),
      successButtonText: new FormControl(null, [Validators.required]),
      successButtonLink: new FormControl(null, [Validators.required]),
    });
  }

  public cancel(): void {
    this.navController.back();
  }

  public async loadImage(): Promise<void> {
    this.image = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Base64,
      quality: 100,
    });
    this.changeDetector.detectChanges();
    this.form.markAsDirty();
  }

  public async loadImageSuccess(): Promise<void> {
    this.imageSuccess = await Camera.getPhoto({
      source: CameraSource.Photos,
      resultType: CameraResultType.Base64,
      quality: 100,
    });
    this.changeDetector.detectChanges();
    this.form.markAsDirty();
  }

  public async removeImage(): Promise<void> {
    if (this.image) this.imageSuccess = null;

    this.surveyDefinition$
      .pipe(
        filter(surveyDefinition => !!surveyDefinition),
        first(),
      )
      .subscribe(async ({ surveyFields, imageUrl, ...rest }) => {
        if (imageUrl) {
          await this.storage.removeFromUrl(imageUrl);
          this.update({ ...rest, imageUrl: null }, false);
        }
      });
  }

  public async removeImageSuccess(): Promise<void> {
    if (this.imageSuccess) this.imageSuccess = null;

    this.surveyDefinition$.pipe(first()).subscribe(async ({ surveyFields, successImageUrl, ...rest }) => {
      if (successImageUrl) {
        await this.storage.removeFromUrl(successImageUrl);
        this.update({ ...rest, successImageUrl: null }, false);
      }
    });
  }

  public async save(): Promise<void> {
    this.store.dispatch(SurveyDefinitionActions.setLoading());
    let imageUrl: string;
    let successImageUrl: string;
    if (this.image) imageUrl = await this.storage.upload(this.image);
    if (this.imageSuccess) successImageUrl = await this.storage.upload(this.imageSuccess);

    const payload: CreateSurveyDefinitionDto = { ...this.form.value, imageUrl, successImageUrl };

    this.surveyDefinitionSlug$.pipe(first()).subscribe(surveyDefinitionSlug => {
      if (surveyDefinitionSlug) this.update(payload, true);
      else this.create(payload);
    });
  }

  protected ionViewWillEnter(): void {
    this.surveyDefinitionSlug$.pipe(first()).subscribe(surveyDefinitionSlug => {
      if (surveyDefinitionSlug) {
        this.surveyDefinition$ = this.store.select(FromSurveyDefinition.selectDefinitionBySlug(surveyDefinitionSlug));
        this.breadcrumbs.push({
          name: this.i18n.instant("ADMIN_SURVEYS.EDIT"),
          link: `/${AppRoutes.adminSurveyDefinitions}/${surveyDefinitionSlug}/${AppRoutes.edit}`,
        });
        this.surveyDefinition$
          .pipe(
            skipWhile(surveyDefinition => !surveyDefinition),
            first(),
          )
          .subscribe(surveyDefinition => this.form.patchValue({ ...surveyDefinition }));
      } else {
        this.breadcrumbs.push({
          name: this.i18n.instant("ADMIN_SURVEYS.CREATE"),
          link: `/${AppRoutes.adminSurveyDefinitions}/${AppRoutes.add}`,
        });
      }
    });
  }

  protected ionViewDidEnter(): void {
    this.surveyDefinitionSlug$
      .pipe(
        filter(surveyDefinitionSlug => !!surveyDefinitionSlug),
        first(),
      )
      .subscribe(definitionSlug => {
        this.store.dispatch(SurveyDefinitionActions.getDefinitionBySlug({ slug: definitionSlug }));
      });
  }

  private create(createSurveyDefinitionDto: CreateSurveyDefinitionDto): void {
    this.store.dispatch(SurveyDefinitionActions.createSurveyDefinition({ createSurveyDefinitionDto }));
  }

  private update(updateSurveyDefinitionDto: UpdateSurveyDefinitionDto, goBack: boolean): void {
    this.surveyDefinition$.pipe(first()).subscribe(({ id }) => {
      this.store.dispatch(SurveyDefinitionActions.updateSurveyDefinition({ definitionId: id, updateSurveyDefinitionDto, goBack }));
    });
  }
}
